import { useEffect, useCallback } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { RealtimeEventType } from '../../services/websocket';

type EventHandler = (...args: any[]) => void;

/**
 * Hook for subscribing to real-time events
 * Provides automatic cleanup on unmount
 *
 * @example
 * ```tsx
 * useRealtimeEvent(RealtimeEventType.NOTIFICATION_NEW, (data) => {
 *   console.log('New notification:', data);
 * });
 * ```
 */
export const useRealtimeEvent = (
  event: RealtimeEventType | string,
  handler: EventHandler
) => {
  const { socket } = useWebSocket();

  useEffect(() => {
    if (!socket) return;

    socket.on(event, handler);

    return () => {
      socket.off(event, handler);
    };
  }, [socket, event, handler]);
};

/**
 * Hook for emitting real-time events
 * Returns a stable emit function
 *
 * @example
 * ```tsx
 * const emitTyping = useRealtimeEmit('user:typing');
 * emitTyping({ tripId: '123' });
 * ```
 */
export const useRealtimeEmit = (event: string) => {
  const { socket } = useWebSocket();

  return useCallback(
    (data?: any) => {
      if (!socket) {
        console.warn('Cannot emit event: WebSocket not connected');
        return;
      }
      socket.emit(event, data);
    },
    [socket, event]
  );
};

/**
 * Hook for checking WebSocket connection status
 */
export const useRealtimeConnection = () => {
  const { isConnected } = useWebSocket();
  return isConnected;
};
